import React, { useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { InstructionListSkeleton } from "@/components/skeletons";
import { BASEDOMAIN } from "@/constants/allconstants";
import { uploadSmalXMLConfigFile } from "@/services/setting.service";
import {
    AZURE_DIRECTORY_FORM,
    COPIED_TIMEOUT,
    FILE_UPLOADED,
    SAML_FILE_TYPE_ERROR,
    SAML_NO_FILE,
} from "@/utils/constants/appConstants/DirectoryConstants";
import { BUTTONS, LABELS, SAML_INSTRUCTION, TEXT } from "@/utils/constants/appConstants/commonConstants";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { successToasterMessage } from "@/utils/functions/toaster";

interface SAMLInstructionProps {
    loader: boolean;
    connector: any;
    handleActivation: (id: string, checked: boolean) => void;
}

const SAMLInstructions = ({ loader, connector, handleActivation }: SAMLInstructionProps) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [copied, setCopied] = useState<string | null>(null);
    const navigate = useNavigate();

    const replyUrl = `${BASEDOMAIN}/auth/saml/redirect?organization=${getCurrentOrganization().organization}`;

    const handleCopy = (field: string) => {
        setCopied(field);
        setTimeout(() => setCopied(null), COPIED_TIMEOUT);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        setSelectedFile(file);
        setErrorMessage("");
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!selectedFile) {
            setErrorMessage(SAML_NO_FILE);
            return;
        }
        const validTypes = ["application/xml", "text/xml"];
        if (!validTypes.includes(selectedFile.type)) {
            setErrorMessage(SAML_FILE_TYPE_ERROR);
            return;
        }
        const formData = new FormData();
        formData.append("xml", selectedFile);
        setUploading(true);
        await uploadSmalXMLConfigFile(
            formData,
            (res: any) => {
                if (res?.status === 201) {
                    setUploading(false);
                    successToasterMessage(FILE_UPLOADED);
                    setSelectedFile(null);
                    navigate("/settings");
                }
            },
            (err: any) => {
                setUploading(false);
                setErrorMessage(err?.response?.data?.message);
            }
        );
    };

    if (loader) return <InstructionListSkeleton />;

    if (connector) {
        return (
            <div className="gs-directory-card__section">
                <div className="gs-directory-field">
                    <label className="gs-directory-field__label" htmlFor="instruction-step-7a">
                        {SAML_INSTRUCTION.step7a}
                        {connector.is_active && <span className="gs-directory-field__label-status">{LABELS.CONNECTED}</span>}
                    </label>
                    <Input
                        type="text"
                        value="greensign"
                        readOnly
                        id="instruction-step-7a"
                        className="gs-directory-field__input gs-directory-field__input--short"
                    />
                </div>
                <div className="gs-directory-field">
                    <label className="gs-directory-field__label" htmlFor="instruction-step-7b">
                        Reply URL:
                    </label>
                    <Input type="text" value={replyUrl} readOnly id="instruction-step-7b" className="gs-directory-field__input" />
                </div>
                <Button
                    type="button"
                    variant="destructive"
                    className="gs-directory-action-btn"
                    onClick={() => handleActivation(connector.id, false)}
                >
                    {AZURE_DIRECTORY_FORM.deactivate_access}
                </Button>
            </div>
        );
    }

    return (
        <div className="gs-directory-card__section">
            <h5 className="gs-directory-section__title" id="saml-instruction-heading">
                {TEXT.SAML_INSTRUCTION_HEADING}
            </h5>
            <ol className="gs-directory-steps">
                <li className="gs-directory-steps__item" id="saml-instruction-1" dangerouslySetInnerHTML={{ __html: SAML_INSTRUCTION.step1 }} />
                <li className="gs-directory-steps__item" id="saml-instruction-2">
                    {SAML_INSTRUCTION.step2}
                </li>
                <li className="gs-directory-steps__item" id="saml-instruction-3">
                    {SAML_INSTRUCTION.step3}
                </li>
                <li className="gs-directory-steps__item" id="saml-instruction-4">
                    {SAML_INSTRUCTION.step4}
                </li>
                <li className="gs-directory-steps__item" id="saml-instruction-5">
                    {SAML_INSTRUCTION.step5}
                </li>
                <li className="gs-directory-steps__item" id="saml-instruction-6">
                    {SAML_INSTRUCTION.step6}
                </li>
                <li className="gs-directory-steps__item" id="saml-instruction-7">
                    {SAML_INSTRUCTION.step7}
                    <ol className="gs-directory-nested-steps">
                        <li>
                            <label className="gs-directory-field__label" htmlFor="instruction-step-7a-setup">
                                {SAML_INSTRUCTION.step7a}
                            </label>
                            <div className="gs-directory-copy-row">
                                <Input
                                    type="text"
                                    value="greensign"
                                    readOnly
                                    id="instruction-step-7a-setup"
                                    className="gs-directory-field__input gs-directory-field__input--short"
                                />
                                <CopyToClipboard text="greensign" onCopy={() => handleCopy("instruction-step-7a")}>
                                    <span className="gs-directory-copy-icon" title={copied === "instruction-step-7a" ? "Copied!" : "Copy to clipboard"}>
                                        <em
                                            id={copied === "instruction-step-7a" ? "instruction-step-7a-copied-icon" : "instruction-step-7a-copy-icon"}
                                            className={`icon ni ${copied === "instruction-step-7a" ? "ni-copy-fill" : "ni-copy"}`}
                                        />
                                    </span>
                                </CopyToClipboard>
                            </div>
                        </li>
                        <li>
                            <label className="gs-directory-field__label" htmlFor="instruction-step-7b-setup">
                                Reply URL:
                            </label>
                            <div className="gs-directory-copy-row">
                                <Input type="text" value={replyUrl} readOnly id="instruction-step-7b-setup" className="gs-directory-field__input" />
                                <CopyToClipboard text={replyUrl} onCopy={() => handleCopy("instruction-step-7b")}>
                                    <span className="gs-directory-copy-icon" title={copied === "instruction-step-7b" ? "Copied!" : "Copy to clipboard"}>
                                        <em
                                            id={copied === "instruction-step-7b" ? "instruction-step-7b-copied-icon" : "instruction-step-7b-copy-icon"}
                                            className={`icon ni ${copied === "instruction-step-7b" ? "ni-copy-fill" : "ni-copy"}`}
                                        />
                                    </span>
                                </CopyToClipboard>
                            </div>
                        </li>
                    </ol>
                </li>
                <li className="gs-directory-steps__item" id="saml-instruction-upload">
                    {SAML_INSTRUCTION.step8}
                    <form onSubmit={handleSubmit} className="gs-directory-upload-form">
                        <input type="file" accept=".xml" onChange={handleFileChange} className="gs-directory-upload-input" />
                        <Button type="submit" id="saml-upload-btn" className="gs-directory-action-btn" disabled={!selectedFile || uploading}>
                            {uploading ? <Spinner size="sm" className="mr-2" /> : null}
                            {BUTTONS.UPLOAD}
                        </Button>
                    </form>
                    {errorMessage ? (
                        <p id="saml-file-upload-err" className="gs-directory-field__error">
                            {errorMessage}
                        </p>
                    ) : null}
                </li>
            </ol>
        </div>
    );
};

export default SAMLInstructions;
